/* eslint-disable no-nested-ternary */
/* eslint-disable react/self-closing-comp */
import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';

import { getConfig } from '@edx/frontend-platform';
import { useToggle } from '@edx/paragon';

import { CourseTabsNavigation } from '../course-tabs';
import { useModel } from '../generic/model-store';
import { AlertList } from '../generic/user-messages';
import StreakModal from '../shared/streak-celebration';
import InstructorToolbar from '../instructor-toolbar';
import useEnrollmentAlert from '../alerts/enrollment-alert';
import useLogistrationAlert from '../alerts/logistration-alert';

import ProductTours from '../product-tours/ProductTours';

const LoadedTabPage = ({
  activeTabSlug,
  children,
  courseId,
  metadataModel,
  unitId,
}) => {
  const {
    celebrations,
    org,
    originalUserIsStaff,
    isMasquerading,
    isCcx,
    tabs,
    title,
    verifiedMode,
  } = useModel('courseHomeMeta', courseId);
  // Logistration and enrollment alerts are only really used for the outline tab, but loaded here to put them above
  // breadcrumbs when they are visible.
  const logistrationAlert = useLogistrationAlert(courseId);
  const enrollmentAlert = useEnrollmentAlert(courseId);

  const activeTab = tabs.filter(tab => tab.slug === activeTabSlug)[0];

  const streakLengthToCelebrate = celebrations && celebrations.streakLengthToCelebrate;
  const streakDiscountCouponEnabled = celebrations && celebrations.streakDiscountEnabled && verifiedMode;
  const [isStreakCelebrationOpen,, closeStreakCelebration] = useToggle(streakLengthToCelebrate);

  /** CampusIL: add custom css file to head of the html */
  const isPreview = window.location.hostname.includes('preview.');
  let assetBaseUrl = courseId ? `${getConfig().STUDIO_BASE_URL}/${courseId.replace('course-v1', 'asset-v1').replace('ccx-v1', 'asset-v1')}` : undefined;
  assetBaseUrl = assetBaseUrl ? (isCcx ? assetBaseUrl.replace(/\+ccx@\d+/, '') : assetBaseUrl) : undefined;
  const assetCustomCSSUrl = assetBaseUrl ? `${assetBaseUrl}+type@asset+block@custom-style.css` : undefined;
  const assetPaletteCSSUrl = isPreview ? undefined : (assetBaseUrl ? `${assetBaseUrl}+type@asset+block@palette-style.css` : undefined);
  const assetPalettePreviewCSSUrl = isPreview ? (assetBaseUrl ? `${assetBaseUrl}+type@asset+block@palette-preview-style.css` : undefined) : undefined;
  const assetPhysicsCSSUrl = assetBaseUrl ? `${assetBaseUrl}+type@asset+block/physics.css` : undefined;
  const assetCustomJavaScriptUrl = assetBaseUrl ? `${assetBaseUrl}+type@asset+block@custom-javascript.js` : undefined;

  const courseTabs = isMasquerading ? tabs.filter((tab) => tab.slug !== 'instructor' && tab.slug !== 'ccx_coach') : tabs;

  return (
    <>
      <ProductTours
        activeTab={activeTabSlug}
        courseId={courseId}
        isStreakCelebrationOpen={isStreakCelebrationOpen}
        org={org}
      />
      <Helmet>
        <title>{`${activeTab ? `${activeTab.title} | ` : ''}${title} | ${getConfig().SITE_NAME}`}</title>
        {assetCustomCSSUrl && <link rel="stylesheet" type="text/css" href={assetCustomCSSUrl} />}
        {assetPaletteCSSUrl && <link rel="stylesheet" type="text/css" href={assetPaletteCSSUrl} />}
        {assetPalettePreviewCSSUrl && <link rel="stylesheet" type="text/css" href={assetPalettePreviewCSSUrl} />}
        {assetPhysicsCSSUrl && <link rel="stylesheet" type="text/css" href={assetPhysicsCSSUrl} />}
        {assetCustomJavaScriptUrl && (<script type="text/javascript" src={assetCustomJavaScriptUrl} defer />)}
      </Helmet>
      {originalUserIsStaff && (
        <InstructorToolbar
          courseId={courseId}
          unitId={unitId}
          tab={activeTabSlug}
          isCcx={isCcx}
        />
      )}
      <StreakModal
        courseId={courseId}
        metadataModel={metadataModel}
        streakLengthToCelebrate={streakLengthToCelebrate}
        isStreakCelebrationOpen={!!isStreakCelebrationOpen}
        closeStreakCelebration={closeStreakCelebration}
        streakDiscountCouponEnabled={streakDiscountCouponEnabled}
        verifiedMode={verifiedMode}
      />
      <main id="main-content" className="d-flex flex-column flex-grow-1">
        <AlertList
          topic="outline"
          className="mx-5 mt-3"
          customAlerts={{
            ...enrollmentAlert,
            ...logistrationAlert,
          }}
        />
        <CourseTabsNavigation tabs={courseTabs} className="mb-3" activeTabSlug={activeTabSlug} />
        <div className="container-xl">
          {children}
        </div>
      </main>
    </>
  );
};

LoadedTabPage.propTypes = {
  activeTabSlug: PropTypes.string.isRequired,
  children: PropTypes.node,
  courseId: PropTypes.string.isRequired,
  metadataModel: PropTypes.string,
  unitId: PropTypes.string,
};

LoadedTabPage.defaultProps = {
  children: null,
  metadataModel: 'courseHomeMeta',
  unitId: null,
};

export default LoadedTabPage;
